import React from 'react';
import { Droplets, Wrench, Sparkles, Package } from 'lucide-react';

const Products = () => {
  const products = [
    {
      id: 1,
      name: 'Drip Irrigation Systems',
      icon: <Droplets className="w-12 h-12" />,
      description: 'Complete drip irrigation solutions for efficient water delivery directly to plant roots',
      features: [
        'Water savings up to 60%',
        'Uniform water distribution',
        'Reduces weed growth',
        'Suitable for all crop types',
        'Easy installation and maintenance'
      ],
      benefits: [
        'Increased crop yield by 20-50%',
        'Lower labor costs',
        'Reduced fertilizer usage',
        'Works on uneven terrain'
      ],
      image: 'https://via.placeholder.com/400x300/8FEC78/ffffff?text=Drip+Irrigation'
    },
    {
      id: 2,
      name: 'Pipes and Fittings',
      icon: <Wrench className="w-12 h-12" />,
      description: 'High-quality HDPE and PVC pipes with complete range of fittings for all irrigation needs',
      features: [
        'Durable HDPE and PVC materials',
        'UV resistant for long life',
        'Various diameter options',
        'Leak-proof connections',
        'Easy to install and repair'
      ],
      benefits: [
        'Long service life (15-20 years)',
        'Corrosion resistant',
        'Lightweight and flexible',
        'Cost-effective solution'
      ],
      image: 'https://via.placeholder.com/400x300/81DD67/ffffff?text=Pipes+%26+Fittings'
    },
    {
      id: 3,
      name: 'Sprinkler Systems',
      icon: <Sparkles className="w-12 h-12" />,
      description: 'Modern sprinkler irrigation systems for uniform water distribution across large areas',
      features: [
        'Multiple spray patterns available',
        'Adjustable water flow',
        'Suitable for large farms',
        'Efficient coverage',
        'Automated operation options'
      ],
      benefits: [
        'Uniform water distribution',
        'Suitable for sandy soils',
        'Reduces labor requirement',
        'Can be used for frost protection'
      ],
      image: 'https://via.placeholder.com/400x300/94F27F/ffffff?text=Sprinkler+Systems'
    },
    {
      id: 4,
      name: 'Irrigation Accessories',
      icon: <Package className="w-12 h-12" />,
      description: 'Complete range of accessories including filters, valves, fertilizer tanks, and controllers',
      features: [
        'Filters for water purification',
        'Control valves and regulators',
        'Fertilizer injection systems',
        'Pressure gauges and meters',
        'Automation controllers'
      ],
      benefits: [
        'Enhanced system efficiency',
        'Better water quality',
        'Precise fertilizer application',
        'Easy system monitoring'
      ],
      image: 'https://via.placeholder.com/400x300/70D958/ffffff?text=Accessories'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-16 px-4" style={{ background: 'var(--gradient-hero)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="heading-1 mb-6">Our Products</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Comprehensive irrigation solutions for modern agriculture
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4" style={{ background: 'var(--bg-page)' }}>
        <div className="max-w-7xl mx-auto space-y-16">
          {products.map((product, index) => (
            <div
              key={product.id}
              className={`grid md:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Product Image */}
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className="product-card overflow-hidden p-0">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>

              {/* Product Details */}
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <div className="flex items-center space-x-3 mb-4" style={{ color: 'var(--accent-text)' }}>
                  {product.icon}
                  <h2 className="heading-2" style={{ fontSize: '2rem' }}>{product.name}</h2>
                </div>
                <p className="body-large mb-6" style={{ color: 'var(--text-body)' }}>
                  {product.description}
                </p>

                {/* Features */}
                <div className="mb-6">
                  <h3 className="heading-3 mb-3" style={{ fontSize: '1.125rem' }}>Key Features:</h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-green-600 font-bold">•</span>
                        <span className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div>
                  <h3 className="heading-3 mb-3" style={{ fontSize: '1.125rem' }}>Benefits:</h3>
                  <ul className="space-y-2">
                    {product.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-blue-600 font-bold">✓</span>
                        <span className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4" style={{ background: 'var(--bg-section)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading-2 mb-4">Need Help Choosing the Right Product?</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Our experts are ready to help you select the best irrigation solution for your farm
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:6382799541" className="btn-primary">
              Call: 6382799541
            </a>
            <a href="tel:7904369704" className="btn-secondary">
              Call: 7904369704
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;